/** One open positional basket: legs, net entry cost, latest mark,
 * holding days, expiry, and the Force-exit kill switch (all legs exited
 * as a unit). Shared by the Dashboard's Carried-baskets card and the
 * positional Strategy Detail page — a basket has no SL/trail machinery,
 * so it never renders through the intraday OpenPositionCard. */

import { useState, type ReactElement } from "react";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { ConfirmDialog } from "@/components/ui/ConfirmDialog";
import { ContractLink } from "@/components/ui/ContractLink";
import { useLiveMtm } from "@/lib/useLiveMtm";
import { tradeLogApi } from "@/lib/tradeLogApi";
import type { TradeLegEntry, TradeLogEntry } from "@/types/tradeLog";

/** Query keys whose views show open baskets — all invalidated after a
 * force-exit so every surface sees the transition. */
const BASKET_VIEW_KEYS = [["carried-baskets"], ["strategy-basket"], ["trade-log"]] as const;

export function BasketTile({ basket }: Readonly<{ basket: TradeLogEntry }>): ReactElement {
  const liveMtm = useLiveMtm(basket);
  return (
    <div className="rounded-lg border border-line p-3">
      <div className="mb-2 flex flex-wrap items-center justify-between gap-2">
        <div className="flex items-center gap-2">
          <span className="font-medium">{basket.strategyName}</span>
          <span className="pill bg-warning-bg text-warning">
            <i className="ti ti-flask text-xs" />
            Paper
          </span>
        </div>
        <ForceExitBasketButton basket={basket} />
      </div>
      <div className="mb-2 space-y-1">
        {(basket.legs ?? []).map((leg) => (
          <LegLine key={leg.legIndex} leg={leg} />
        ))}
      </div>
      <div className="flex flex-wrap gap-x-4 gap-y-1 text-xs text-ink-secondary">
        <Stat label="Entered" value={enteredAt(basket.entryAt)} />
        <Stat label="Net cost" value={netCost(basket.netEntryCost)} />
        <Stat
          label={liveMtm !== null ? "MTM (live)" : "MTM"}
          value={mtm(liveMtm ?? basket.lastMtm)}
          tone={mtmTone(liveMtm ?? basket.lastMtm)}
        />
        <Stat label="Held" value={`${String(basket.holdingDays ?? 0)}d`} />
        <Stat label="Expiry" value={basket.basketExpiry ?? "—"} />
      </div>
    </div>
  );
}

function LegLine({ leg }: Readonly<{ leg: TradeLegEntry }>): ReactElement {
  const buy = leg.side === "BUY";
  return (
    <div className="flex items-center justify-between font-mono text-xs">
      <span>
        <span className={buy ? "text-success" : "text-danger"}>{buy ? "+" : "−"}</span>{" "}
        <ContractLink symbol={leg.contractSymbol} expiry={leg.expiry} />
      </span>
      <span className="text-ink-muted">
        {String(leg.quantity)} @ {leg.entryFillPrice !== null ? leg.entryFillPrice.toFixed(2) : "—"}
      </span>
    </div>
  );
}

function Stat({
  label,
  value,
  tone,
}: Readonly<{ label: string; value: string; tone?: string | undefined }>): ReactElement {
  return (
    <span>
      <span className="text-ink-muted">{label}:</span> <span className={tone}>{value}</span>
    </span>
  );
}

function mtmTone(value: number | null): string | undefined {
  if (value === null || value === 0) return undefined;
  return value > 0 ? "text-success" : "text-danger";
}

/** IST entry stamp — date AND clock time (a basket is held for days, so
 * the date matters as much as the time). */
function enteredAt(iso: string): string {
  return new Date(iso).toLocaleString("en-IN", {
    day: "numeric",
    month: "short",
    hour: "2-digit",
    minute: "2-digit",
    hour12: false,
    timeZone: "Asia/Kolkata",
  });
}

function netCost(cost: number | null): string {
  if (cost === null) return "—";
  const rupees = Math.abs(Math.round(cost)).toLocaleString("en-IN");
  return `Rs ${rupees} ${cost >= 0 ? "debit" : "credit"}`;
}

function mtm(value: number | null): string {
  if (value === null) return "pending";
  const rounded = Math.round(value);
  let sign = "";
  if (rounded > 0) sign = "+";
  else if (rounded < 0) sign = "-";
  return `${sign}Rs ${Math.abs(rounded).toLocaleString("en-IN")}`;
}

function ForceExitBasketButton({ basket }: Readonly<{ basket: TradeLogEntry }>): ReactElement {
  const qc = useQueryClient();
  const [confirming, setConfirming] = useState(false);
  const mut = useMutation({
    mutationFn: () => tradeLogApi.forceExit(basket.id),
    onSettled: () => {
      for (const key of BASKET_VIEW_KEYS) {
        void qc.invalidateQueries({ queryKey: key });
      }
      setConfirming(false);
    },
  });
  return (
    <>
      <button
        type="button"
        className="btn btn-danger text-xs"
        onClick={() => {
          setConfirming(true);
        }}
      >
        <i className="ti ti-hand-stop text-sm" />
        Force exit
      </button>
      <ConfirmDialog
        open={confirming}
        title="Force-exit this basket?"
        description={
          `All ${String(basket.legs?.length ?? 0)} legs are exited as a unit — short legs ` +
          "bought back first. The positional poller confirms the fills to CLOSED."
        }
        confirmLabel="Exit basket"
        icon="ti-hand-stop"
        tone="danger"
        busy={mut.isPending}
        onCancel={() => {
          setConfirming(false);
        }}
        onConfirm={() => {
          mut.mutate();
        }}
      />
    </>
  );
}
